// Complete Master Registry Entries from v5.5
// These are the ACTUAL frameworks, models, and systems in the registry

export interface RegistryEntry {
  canonicalName: string;
  encodedId: string;
  shortCode: string;
  elements: string;
  section: string;
  sectionCode: string;
  category?: string; // e.g., 'Type 2: Clarity & Alignment'
  subcategory?: string; // P-Stack: 'Phases & Flows', 'Objects, Domains & App Spaces', etc. | G-Stack: 'Foundational Grammar & Ontology', 'Representation & Modeling Techniques', etc.
  enhancedFramework?: boolean; // true if this has enhanced M2 data
  version?: number; // version number (1, 2, 3, etc.) based on content and fields available
  docStatus?: string; // [DOC:D#] - D0=None, D1=Stub, D2=Draft, D3=Complete
  matStatus?: string; // [MAT:A#] - A0=Idea, A1=Exploration, A2=Prototype, A3=Pilot, A4=Adopted/Production, A5=Institutionalized
  
  // Bounded Attributes - M3 Grammar Pattern Integration
  boundedAttributes?: {
    [attributeId: string]: string; // Maps bounded attribute ID to its value
  };
  
  // Additional metadata for bounded attributes
  primaryBoundedAttributes?: string[]; // List of primary bounded attributes this model uses
  secondaryBoundedAttributes?: string[]; // List of secondary bounded attributes this model references
  
  // Bounded attribute category
  boundedAttributeCategory?: 'pure-enumeration' | 'internal-component' | 'connector';
}

export const masterRegistryEntries: RegistryEntry[] = [
  // G-STACK: GENERAL FRAMEWORKS & MODELS (gf)
  {
    canonicalName: 'Strategy-Delivery Cascade by Scope',
    encodedId: 'mof.strategy_delivery_cascade_by_scope',
    shortCode: 'SDC×S',
    elements: '4 Phases (Strategy, Prioritization, Planning, Delivery) × 2 Scopes (Individual, Organization/Group) = 8 contexts',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Strategic Architecture',
    category: 'Type 2: Clarity & Alignment',
    enhancedFramework: true,
    version: 1
  },
  // P-STACK: PRIORITIZATION FRAMEWORKS & MODELS (pf)
  {
    canonicalName: 'Five Stages of DEGAP',
    encodedId: 'pf.degap.stages',
    shortCode: 'DEGAP-5',
    elements: '1) Decide  2) Engage  3) Gather  4) Arrange  5) Prioritize',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Phases & Flows',
    version: 1,
    boundedAttributes: {
      'degap-phase': 'Decide, Engage, Gather, Arrange, Prioritize',
      'lifecycle-stage': 'Intake, Item Work, Ranking, Execution',
      'scope-level': 'Individual, Team, Organization'
    },
    primaryBoundedAttributes: ['degap-phase', 'lifecycle-stage'],
    secondaryBoundedAttributes: ['scope-level', 'role-type'],
    boundedAttributeCategory: 'internal-component'
  },
  {
    canonicalName: 'Max Priorities Pyramid',
    encodedId: 'pf.max.priorities.pyramid',
    shortCode: 'MPP',
    elements: 'Hierarchical prioritization framework with base foundations, middle strategic layers, and peak maximum priorities',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Strategic Architecture',
    version: 2
  },
  {
    canonicalName: 'Prioritization Objects & Relationships',
    encodedId: 'pf.objects.relationships',
    shortCode: 'POR',
    elements: '1. Item: The primary first-class object; workflow stages Intake → Item Work → Ranking; ranked position is contextual (Item Pool + Session). Defined by Attributes/Values, belongs to Item Pools, managed by Ball Holder. 2. Attribute/Value: Define Items (Attributes=fields like Impact/Effort/Risk, Values=entries for specific Item). 4. Criteria: Selected Attributes used for evaluation/comparison within a Criteria Set, governed by Decision Authority. 5. Item Pool: The comparison set selected during Arrange; locked before S6. 6. Cycle: The highest-level container of intent for prioritization. 7. Track: A prioritization lane defined by (Item Category × Altitude).',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Objects, Domains & App Spaces',
    version: 1,
    docStatus: 'D2',
    matStatus: 'A2',
    boundedAttributes: {
      'item-state': 'Active, Archived, Discarded',
      'workflow-stage': 'Intake, Item Work, Prerequisites Satisfied',
      'attribute-type': 'Impact, Effort, Risk, Value, Urgency',
      'governance-role': 'Ball Holder, Cycle Owner, Decision Authority, Facilitator',
      'track-cell': '(Item Category × Altitude)',
      'comparison-set': 'Single Item Pool, Cross-Pool, Cross-Track'
    },
    primaryBoundedAttributes: ['candidate-state', 'lifecycle-stage', 'attribute-type'],
    secondaryBoundedAttributes: ['governance-role', 'focus-horizon', 'comparison-set'],
    boundedAttributeCategory: 'internal-component'
  },
  {
    canonicalName: 'Six Kinds of Items',
    encodedId: 'pf.items.kinds',
    shortCode: '6-Kinds',
    elements: '1) People: Categories of people, groups, or constituencies\n2) Places: Locations, both real and virtual: sites, queues, etc.\n3) Things: Material things, virtual, or imaginary objects: products, services, tools, etc.\n4) Activities: Projects, initiatives, actions, and tasks\n5) Information: Any type of data at any level of abstraction, quantifiable or subjective: goals, objectives, ideas, values, complaints, symptoms, needs, questions, risk factors, etc.\n6) Time: Processes: phases, workflows, sequencing, system effects, and transitions. (Source: Managing Priorities)',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Objects, Domains & App Spaces',
    boundedAttributes: {
      'prioritization-domain': 'People, Places, Things, Activities, Information, Time'
    },
    primaryBoundedAttributes: ['prioritization-domain'],
    secondaryBoundedAttributes: ['scope-level', 'role-type']
  },
  {
    canonicalName: 'Strategy-Delivery Cascade',
    encodedId: 'pf.strategy_delivery.phases',
    shortCode: 'SDC-4P',
    elements: '1) Strategy  2) Prioritization  3) Planning  4) Delivery',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Phases & Flows',
    version: 4,
    boundedAttributes: {
      'scope-level': 'Individual, Team, Organization',
      'phase': 'Strategy, Prioritization, Planning, Delivery',
      'mode-stars': 'Starting Something New, Necessary Ending, Turnaround, Scaling, Sustaining/Advancing',
      'resolution-level': 'Phrase, Sentence, Paragraph, Page',
      'evidence-range': 'Barely Sufficient, Target, Hopelessly Idealistic',
      'admat': 'A0, A1, A2, A3, A4, A5'
    },
    primaryBoundedAttributes: ['phase'],
    secondaryBoundedAttributes: ['scope-level', 'mode-stars', 'resolution-level', 'evidence-range', 'admat'],
    boundedAttributeCategory: 'internal-component'
  },
  {
    canonicalName: 'Prioritization Diagnostic Framework',
    encodedId: 'pf.framework.diagnostic',
    shortCode: 'PDF',
    elements: 'See decomposition below',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics'
  },
  // Prioritization Diagnostic Framework - Decomposition
  {
    canonicalName: 'Contextual Modes (STARS)',
    encodedId: 'pf.framework.diagnostic.context.modes',
    shortCode: 'STARS',
    elements: '1) Starting Something New  2) Approaching a Necessary Ending  3) Launching a Turnaround  4) Scaling Up or Down  5) Sustaining or Advancing Performance',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    boundedAttributes: {
      'contextual-mode': 'Starting New, Necessary Ending, Turnaround, Scaling, Sustaining'
    },
    primaryBoundedAttributes: ['contextual-mode'],
    secondaryBoundedAttributes: ['scope-level', 'role-type', 'degap-phase']
  },
  {
    canonicalName: 'DEGAP Phases',
    encodedId: 'pf.framework.diagnostic.degap.phases',
    shortCode: 'DEGAP',
    elements: '1) Decide  2) Engage  3) Gather  4) Arrange  5) Prioritize',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Phases & Flows'
  },
  {
    canonicalName: 'Role Types',
    encodedId: 'pf.framework.diagnostic.role.types',
    shortCode: 'Roles',
    elements: '1) Individual Contributor  2) Team Lead / Manager  3) Executive / Org Leader',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    boundedAttributes: {
      'role-type': 'Individual Contributor, Team Lead/Manager, Executive/Org Leader'
    },
    primaryBoundedAttributes: ['role-type'],
    secondaryBoundedAttributes: ['scope-level'],
    boundedAttributeCategory: 'pure-enumeration'
  },
  {
    canonicalName: 'Scope Levels',
    encodedId: 'pf.framework.diagnostic.scope.levels',
    shortCode: 'Scopes',
    elements: '1) Individual  2) Team  3) Organization',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    version: 4,
    boundedAttributes: {
      'scope-level': 'Individual, Team, Organization',
      'phase': 'Strategy, Prioritization, Planning, Delivery, Decide, Engage, Gather, Arrange, Prioritize, Intake, Item Work, Ranking, Execution',
      'mode-stars': 'Starting Something New, Necessary Ending, Turnaround, Scaling, Sustaining/Advancing',
      'resolution-level': 'Phrase, Sentence, Paragraph, Page',
      'admat': 'A0, A1, A2, A3, A4, A5'
    },
    primaryBoundedAttributes: ['scope-level'],
    secondaryBoundedAttributes: ['phase', 'mode-stars', 'resolution-level', 'admat'],
    boundedAttributeCategory: 'pure-enumeration'
  },
  {
    canonicalName: 'Scope Levels (2-Level)',
    encodedId: 'pf.framework.diagnostic.scope.levels.2level',
    shortCode: 'Scopes-2L',
    elements: '1) Individual  2) Organization/Group',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics'
  },
  {
    canonicalName: 'Role × Scope Matrix',
    encodedId: 'pf.framework.diagnostic.role_scope.matrix',
    shortCode: 'Role×Scope',
    elements: 'Cross-mapping of the three Roles × three Scopes',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    version: 4,
    boundedAttributes: {
      'scope-level': 'Individual, Team, Organization',
      'phase': 'Strategy, Prioritization, Planning, Delivery, Decide, Engage, Gather, Arrange, Prioritize, Intake, Item Work, Ranking, Execution',
      'mode-stars': 'Starting Something New, Necessary Ending, Turnaround, Scaling, Sustaining/Advancing',
      'resolution-level': 'Phrase, Sentence, Paragraph, Page',
      'admat': 'A0, A1, A2, A3, A4, A5'
    },
    primaryBoundedAttributes: ['scope-level'],
    secondaryBoundedAttributes: ['phase', 'mode-stars', 'resolution-level', 'admat'],
    boundedAttributeCategory: 'connector'
  },
  {
    canonicalName: 'Capability Dimensions (12)',
    encodedId: 'pf.framework.diagnostic.capabilities.12',
    shortCode: '12-Caps',
    elements: 'Situational Awareness, Rhythm & Ritual, Clarity of Focus, Process Discipline, Stakeholder Intelligence, Avoidance & Activation, Cost of Delay Thinking, Tool Fluency, Conceptual Precision, Cultural Reinforcement, Tradeoff Intelligence, Outcome Reflection',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    version: 4,
    boundedAttributes: {
      'scope-level': 'Individual, Team, Organization',
      'phase': 'Decide, Engage, Gather, Arrange, Prioritize',
      'mode-stars': 'Starting Something New, Necessary Ending, Turnaround, Scaling, Sustaining/Advancing',
      'resolution-level': 'Phrase, Sentence, Paragraph, Page',
      'admat': 'A0, A1, A2, A3, A4, A5'
    },
    primaryBoundedAttributes: ['scope-level'],
    secondaryBoundedAttributes: ['phase', 'mode-stars', 'resolution-level', 'admat'],
    boundedAttributeCategory: 'connector'
  },
  {
    canonicalName: 'Unified Capability Expressions',
    encodedId: 'pf.framework.diagnostic.diagnostics.unified',
    shortCode: 'UnifiedDiag',
    elements: 'Each of the 12 capabilities expressed at Individual, Team, and Org levels',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    docStatus: 'D1',
    matStatus: 'A1'
  },
  {
    canonicalName: 'Capability Families (4)',
    encodedId: 'pf.framework.diagnostic.capabilities.families',
    shortCode: '4-Families',
    elements: 'Contextual Awareness, Execution Rhythms, Relational Navigation, Motivational Activation',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    version: 4,
    boundedAttributes: {
      'scope-level': 'Individual, Team, Organization',
      'phase': 'Decide, Engage, Gather, Arrange, Prioritize',
      'mode-stars': 'Starting Something New, Necessary Ending, Turnaround, Scaling, Sustaining/Advancing',
      'resolution-level': 'Phrase, Sentence, Paragraph, Page',
      'admat': 'A0, A1, A2, A3, A4, A5'
    },
    primaryBoundedAttributes: ['scope-level'],
    secondaryBoundedAttributes: ['phase', 'mode-stars', 'resolution-level', 'admat'],
    boundedAttributeCategory: 'connector'
  },
  {
    canonicalName: 'Relationships Model',
    encodedId: 'pf.framework.diagnostic.relationships.model',
    shortCode: 'Relns',
    elements: 'Contextual Modes → DEGAP → Capabilities → Role/Scope → Unified Diagnostics',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics'
  },
  {
    canonicalName: 'Self vs System Lens',
    encodedId: 'pf.framework.diagnostic.lens.self_system',
    shortCode: 'Self↔System',
    elements: 'Self-Oriented vs System-Oriented capabilities',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics'
  },
  {
    canonicalName: 'Sense–Align vs Activate–Execute Lens',
    encodedId: 'pf.framework.diagnostic.lens.sense_activate',
    shortCode: 'S&A↔A&E',
    elements: 'Sense & Align vs Activate & Execute capability families',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics'
  },
  {
    canonicalName: 'Role-Based Participation Across Scopes',
    encodedId: 'pf.framework.diagnostic.role_participation',
    shortCode: 'RoleXScope',
    elements: 'Matrix of how Individual Contributors, Team Leads/Managers, and Executives engage differently across Individual, Team, and Organizational prioritization levels. Each role type participates at all three levels, but the nature of their participation shifts based on scope of influence.',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics'
  },

  // G-STACK: GENERAL FRAMEWORKS & MODELS (gf)
  {
    canonicalName: 'The Relevance Framework',
    encodedId: 'gf.relevance.framework',
    shortCode: 'What-Why-How',
    elements: '1) What is it? (Define the essence)  2) Why is it important? (Connect to significance)  3) How does it work? (Demonstrate mechanics)',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Communication & Clarity'
  },
  {
    canonicalName: 'Four-Level Diagramming Taxonomy (The Fourfold Stack)',
    encodedId: 'gf.diagram.levels',
    shortCode: '4-Levels',
    elements: 'Vision Map, Architecture Map, Enhanced Process Flow Map, Implementation Blueprint',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Representation & Modeling Techniques'
  },
  {
    canonicalName: 'Six Primitives of System Modeling',
    encodedId: 'gf.system.primitives',
    shortCode: '6-Prims',
    elements: '1) Actors – Who is involved (users, systems, roles)\n2) Components – What it\'s made of (UI, APIs, services, databases)\n3) Processes – What happens (workflows, transitions, logic)\n4) Data – What it stores and relates (entities, attributes, relationships)\n5) Interactions – What moves or triggers (actions, events, alerts)\n6) Context of Purpose – Why it matters (scenario, outcome, job-to-be-done)',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology'
  },
  {
    canonicalName: 'Interrogative Dimensions Framework (5W+Extent)',
    encodedId: 'idf.core.set',
    shortCode: '5W+X',
    elements: 'Who, What, When, Where, Why, How, To-What-Extent',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology'
  },
  {
    canonicalName: 'EIT: Time/When Taxonomy',
    encodedId: 'gf.eit.time.when',
    shortCode: 'EIT-Time',
    elements: 'Core Time Objects: Instant, Interval, Reservation, Rhythm, Constraint, Trigger, Dependency, Queue, Buffer, Milestone. Commitment Types: decision_time, earliest_start, latest_responsible_start, handoff_time, delivery_time, review_by, accept_by, deadline, release_time, expiry_time. Time Quantities: elapsed_duration, effort_time, lead_time, lag_time, cycle_time, throughput. Application: Universal - use when defining "when" aspects of priorities, especially in the Arrange phase of DEGAP.',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology',
    docStatus: 'D2',
    matStatus: 'A2'
  },
  {
    canonicalName: 'EIT: Actor/Who Taxonomy',
    encodedId: 'gf.eit.actor.who',
    shortCode: 'EIT-Actor',
    elements: 'Core Actor Objects: Role, Actor, Authority, Accountability, Stakeholder. Commitment Types: accountable_owner, approver, decision_maker, consulted_party, informed_party, delegate_of, proxy_for. Quantities: capacity_hours, load_ratio, focus_allocation_pct, skill_proficiency_level. Application: Essential for Stakeholder Intelligence capability and DEGAP Engage phase.',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology',
    docStatus: 'D2',
    matStatus: 'A2'
  },
  {
    canonicalName: 'EIT: Object/What Taxonomy',
    encodedId: 'gf.eit.object.what',
    shortCode: 'EIT-Object',
    elements: 'Core Work Objects: Work Item, Deliverable, Requirement, Decision Record, Dependency. Commitment Types: scope_statement, acceptance_criteria, definition_of_done, change_request, exception_waiver, version_tag. Quantities: size_points, complexity_score, value_score, risk_exposure. Application: Critical for Clarity of Focus capability and DEGAP Gather/Arrange phases.',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology',
    docStatus: 'D2',
    matStatus: 'A2'
  },
  {
    canonicalName: 'EIT: Why/Rationale Taxonomy',
    encodedId: 'gf.eit.rationale.why',
    shortCode: 'EIT-Why',
    elements: 'Rationale Objects: Business Value, Strategic Alignment, Risk Mitigation, Compliance Requirement, Stakeholder Need. Commitment Types: value_proposition, success_criteria, risk_assessment, compliance_check, stakeholder_agreement. Quantities: value_score, alignment_score, risk_level, compliance_status. Application: Essential for Outcome Reflection capability and DEGAP Decide phase.',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology',
    docStatus: 'D1',
    matStatus: 'A1'
  },
  {
    canonicalName: 'EIT: How/Method Taxonomy',
    encodedId: 'gf.eit.method.how',
    shortCode: 'EIT-How',
    elements: 'Method Objects: Process, Tool, Technique, Framework, Practice. Commitment Types: process_step, tool_usage, technique_application, framework_adoption, practice_standard. Quantities: process_efficiency, tool_proficiency, technique_mastery, framework_adoption_rate. Application: Critical for Tool Fluency capability and DEGAP Engage/Gather phases.',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology',
    docStatus: 'D1',
    matStatus: 'A1'
  },
  {
    canonicalName: 'EIT: Where/Location Taxonomy',
    encodedId: 'gf.eit.location.where',
    shortCode: 'EIT-Where',
    elements: 'Location Objects: Physical Space, Digital Space, Organizational Unit, Geographic Region, Market Segment. Commitment Types: location_assignment, space_reservation, unit_assignment, region_coverage, market_targeting. Quantities: space_capacity, geographic_coverage, market_penetration, organizational_reach. Application: Important for Situational Awareness capability and DEGAP Gather phase.',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology',
    docStatus: 'D1',
    matStatus: 'A1'
  },
  {
    canonicalName: 'EIT: Extent/Degree Taxonomy',
    encodedId: 'gf.eit.extent.degree',
    shortCode: 'EIT-Extent',
    elements: 'Extent Objects: Scope Boundary, Quality Threshold, Performance Target, Capacity Limit, Priority Level. Commitment Types: scope_boundary, quality_standard, performance_target, capacity_constraint, priority_assignment. Quantities: scope_size, quality_score, performance_metric, capacity_utilization, priority_weight. Application: Essential for Tradeoff Intelligence capability and DEGAP Arrange phase.',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology',
    docStatus: 'D1',
    matStatus: 'A1'
  },
  {
    canonicalName: 'EIT: Cross-Dimension Integration',
    encodedId: 'gf.eit.integration.cross_dimension',
    shortCode: 'EIT-Integration',
    elements: 'Essential Relationships: who × what (responsibility), who × when (availability), what × when (schedule). Integration Patterns: responsibility(role, work_item, RACI_type), availability(actor, time_window), schedule(work_item, timeline)',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology',
    docStatus: 'D2',
    matStatus: 'A2'
  },
  {
    canonicalName: 'OODA Loop (Observe-Orient-Decide-Act)',
    encodedId: 'gf.ooda.loop',
    shortCode: 'OODA',
    elements: '4 Stages: Observe (gather information), Orient (contextualize and filter), Decide (make choice or pause), Act (execute and test)',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Decision Cycles & Orientation',
    version: 1
  },
  {
    canonicalName: 'Tool Function Families',
    encodedId: 'gf.tools.families',
    shortCode: 'ToolFns',
    elements: 'Sensemaking Guides, Clarity Sharpeners, Trade-off Balancers, Alignment Engines, Activation Catalysts, Process Mirrors, Rhythm & Capacity Builders, Perspective Shifters',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf'
  },

  // ARCHIVED: KNOWLEDGE ENGINE / BACKSTAGE (ke)
  // {
  //   canonicalName: 'TrailGuide Engine',
  //   encodedId: 'ke.trailguide.engine',
  //   shortCode: 'TrailGuide',
  //   elements: 'Provides system-level navigation/overview',
  //   section: 'Knowledge Engine / Backstage',
  //   sectionCode: 'ke',
  //   subcategory: 'System Navigation'
  // },

  // PRIORITIZATION APP FRAMEWORKS & MODELS (pa)
  {
    canonicalName: 'Twelve Application Modeling Spaces',
    encodedId: 'pa.modeling.spaces',
    shortCode: '12-Spaces',
    elements: 'Item Intake & Classification, Item Work, Attribute Management, Criteria Selection & Comparison Set Framing, Item Pool Creation & Framing, Item Pool Editing & Cut Line Management, Prioritization Session Support, Above-the-Line Enforcement & Drift Detection, Realignment & Change Reconciliation, Cycle Navigation & Visualization, System Settings & Governance Layer, Analytics & Insight Layer',
    section: 'P-Stack: App Frameworks',
    sectionCode: 'pa',
    subcategory: 'Objects, Domains & App Spaces'
  },
  {
    canonicalName: 'Four Lifecycle Stages',
    encodedId: 'pa.lifecycle.stages',
    shortCode: '4-Stages',
    elements: 'Intake, Item Work, Ranking, Execution',
    section: 'P-Stack: App Frameworks',
    sectionCode: 'pa',
    subcategory: 'Phases & Flows',
    version: 4,
    boundedAttributes: {
      'scope-level': 'Individual, Team, Organization',
      'phase': 'Intake, Item Work, Ranking, Execution',
      'mode-stars': 'Starting Something New, Necessary Ending, Turnaround, Scaling, Sustaining/Advancing',
      'resolution-level': 'Phrase, Sentence, Paragraph, Page',
      'evidence-range': 'Barely Sufficient, Target, Hopelessly Idealistic',
      'admat': 'A0, A1, A2, A3, A4, A5'
    },
    primaryBoundedAttributes: ['phase'],
    secondaryBoundedAttributes: ['scope-level', 'mode-stars', 'resolution-level', 'evidence-range', 'admat'],
    boundedAttributeCategory: 'internal-component'
  },
  {
    canonicalName: 'Six-Layer Prioritization System Architecture',
    encodedId: 'pf.architecture.layers',
    shortCode: '6-Layers',
    elements: 'Input, Structure, Action, Execution, System Coordination, Measurement & Optimization',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'System Architecture'
  },

  // ARCHIVED: P2 PLATFORM ENGINES (p2)
  // {
  //   canonicalName: 'Platform Engines',
  //   encodedId: 'p2.engines.set',
  //   shortCode: 'Engines',
  //   elements: 'Prospector, Curation, Collaboration, Authoring, Canon, Knowledge Engine, Media Engine, Assembly Engine, Publishing Engine, Education Engine, Virtual Studio, Influence Engine, Orchestration, Analytics & Feedback, Identity & Access, Simulation',
  //   section: 'P2 Platform Engines',
  //   sectionCode: 'p2',
  //   subcategory: 'Platform Infrastructure'
  // },

  // META STACK & PERCEPTION (meta)
  {
    canonicalName: 'Four-Layer Meta-Stack',
    encodedId: 'meta.stack.4l',
    shortCode: 'Meta-4L',
    elements: 'Ontology (what exists), Modality (how properties vary), Dynamics (how things change), Evaluation (why it matters)',
    section: 'Meta Stack & Perception',
    sectionCode: 'meta',
    subcategory: 'Foundational Grammar & Ontology'
  },
  {
    canonicalName: 'State Topology',
    encodedId: 'meta.modality.state.topology',
    shortCode: 'StateTop',
    elements: 'Toggle, Dial, Multi-State, Nested System',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    subcategory: 'Foundational Grammar & Ontology',
    enhancedFramework: true,
    version: 3,
    docStatus: 'D3',
    matStatus: 'A4'
  },
  {
    canonicalName: 'Eleven Perceived Dimensions',
    encodedId: 'meta.perception.11',
    shortCode: '11-Dims',
    elements: 'Space, Time, Quantity/Magnitude, Causation/Regularity, Object Identity/Persistence, Affect: Valence, Affect: Arousal, Social Warmth/Trustworthiness, Social Competence/Capability, Agency/Controllability, Uncertainty/Precision',
    section: 'Meta Stack & Perception',
    sectionCode: 'meta',
    subcategory: 'Foundational Grammar & Ontology'
  },

  // CONTENT & SERVICES
  {
    canonicalName: 'Content Creation and Publishing Pipeline',
    encodedId: 'cont.creation.publishing.pipeline',
    shortCode: 'CCPP',
    elements: '6 Stages: Gathering, Tagging, Formulating, Creating, Applying, Publishing',
    section: 'Content & Services',
    sectionCode: 'cont',
    subcategory: 'Content & Enablement'
  },
  {
    canonicalName: 'Multi-Resolution Content Levels',
    encodedId: 'cont.multires.levels',
    shortCode: 'MultiRes',
    elements: 'Phrase, Sentence, Paragraph, Page',
    section: 'Content & Services',
    sectionCode: 'cont',
    subcategory: 'Content & Enablement'
  },
  {
    canonicalName: 'Services & Offerings',
    encodedId: 'svc.offerings.set',
    shortCode: 'Offerings',
    elements: 'Workshops, Speaking, Courses, Consulting',
    section: 'Content & Services',
    sectionCode: 'svc',
    subcategory: 'Service Delivery'
  },
  
  // CLARITY & ALIGNMENT FRAMEWORKS (Enhanced M2 Models)
  {
    canonicalName: 'Desired Outcome Framework',
    encodedId: 'gf.desired.outcome',
    shortCode: 'DOF',
    elements: 'Core Question, Evidence Continuum (Barely Sufficient, Target, Hopelessly Idealistic), Question Zero, Rock Fetch Story',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    category: 'Type 2: Clarity & Alignment',
    subcategory: 'Outcome Definition & Evaluation',
    enhancedFramework: true,
    version: 2,
    docStatus: 'D3',
    matStatus: 'A4'
  },
  {
    canonicalName: 'Outcome Driver Framework',
    encodedId: 'gf.outcome.driver',
    shortCode: 'ODF',
    elements: 'Current State, Problem Statement, Consequence/Impact/Value Judgment',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    category: 'Type 2: Clarity & Alignment',
    enhancedFramework: true,
    version: 2
  },
  // Version 3 Models - Advanced Awareness Frameworks
  {
    canonicalName: 'Johari Window Framework',
    encodedId: 'pf.awareness.johari',
    shortCode: 'Johari',
    elements: 'Open Area, Blind Spot, Hidden Area, Unknown Area',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    category: 'Type 3: Advanced Models',
    subcategory: 'Cognitive & Insight Frameworks',
    enhancedFramework: true,
    version: 3
  },
  {
    canonicalName: 'Logical Levels Framework',
    encodedId: 'pf.awareness.logical',
    shortCode: 'LogLevels',
    elements: 'Environment, Behavior, Capabilities, Beliefs/Values, Identity, Spirituality',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    category: 'Type 3: Advanced Models',
    subcategory: 'Cognitive & Insight Frameworks',
    enhancedFramework: true,
    version: 3
  },
  {
    canonicalName: 'Ladder of Inference Framework',
    encodedId: 'pf.awareness.ladder',
    shortCode: 'Ladder',
    elements: 'Observable Data, Selected Data, Added Meaning, Assumptions, Conclusions, Beliefs, Actions',
    section: 'G-Stack: General Frameworks',
    sectionCode: 'gf',
    category: 'Type 3: Advanced Models',
    subcategory: 'Cognitive & Insight Frameworks',
    enhancedFramework: true,
    version: 3
  },
  // OPEN QUESTIONS FRAMEWORK (OQF) - M2 Level
  {
    canonicalName: 'Open Questions Framework',
    encodedId: 'oqf.framework',
    shortCode: 'OQF',
    elements: 'A structured method for moving from uncertainty to clarity and accelerating learning. Five functions: Frame the Question, Surface Current Theory, Identify Source of Truth, Define Path of Inquiry, Review & Reflect.',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    category: 'Type 2: Clarity & Alignment',
    version: 1,
    docStatus: 'D3',
    matStatus: 'A4'
  },
  {
    canonicalName: 'Open Questions Framework Workshop',
    encodedId: 'oqf.workshop',
    shortCode: 'OQF-WS',
    elements: '7-module workshop teaching the Open Questions Framework: 1) Diagnose & Frame, 2) Surface & Label Assumptions, 3) Define Path of Inquiry, 4) Prioritize Questions, 5) Maintain OQ Register, 6) Measure Clarity Acceleration, 7) Make OQF a Habit',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    category: 'Type 2: Clarity & Alignment',
    version: 1,
    docStatus: 'D3',
    matStatus: 'A3'
  },
  {
    canonicalName: '3-Point OQF Question Checklist',
    encodedId: 'oqf.tool.3-point-checklist',
    shortCode: 'OQF-3P',
    elements: 'Tool for framing high-leverage unknowns: 1) Singular Unknown, 2) Decision-Linked, 3) Neutral & Investigable',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    category: 'Type 2: Clarity & Alignment',
    version: 1,
    docStatus: 'D3',
    matStatus: 'A4'
  },
  {
    canonicalName: 'Evidence-Quality Scale',
    encodedId: 'oqf.tool.evidence-quality',
    shortCode: 'OQF-EQS',
    elements: '4-level scale for labeling evidence quality: 1) Anecdote, 2) Directional, 3) Strong, 4) Verified',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    category: 'Type 2: Clarity & Alignment',
    version: 1,
    docStatus: 'D3',
    matStatus: 'A4'
  },
  {
    canonicalName: 'Smallest Decisive Experiment (SDE)',
    encodedId: 'oqf.tool.sde',
    shortCode: 'OQF-SDE',
    elements: 'Minimum viable learning step with 3 criteria: 1) Small (fast signal), 2) Decisive (clear if-then rule), 3) Truth-Oriented (tests real unknown)',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    category: 'Type 2: Clarity & Alignment',
    version: 1,
    docStatus: 'D3',
    matStatus: 'A4'
  },
  {
    canonicalName: 'OQF Prioritization Rubric',
    encodedId: 'oqf.tool.prioritization-rubric',
    shortCode: 'OQF-PR',
    elements: 'Leverage = (Impact × Dependency) ÷ Effort-to-Learn. Scores 1-5 for each variable to rank open questions',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    category: 'Type 2: Clarity & Alignment',
    version: 1,
    docStatus: 'D3',
    matStatus: 'A4'
  },
  {
    canonicalName: 'Open Questions Register',
    encodedId: 'oqf.tool.register',
    shortCode: 'OQF-REG',
    elements: 'Operating system for tracking open questions with fields: Question, Owner, Current Beliefs, Evidence Quality, Next Step (SDE), Review Date, Status',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    category: 'Type 2: Clarity & Alignment',
    version: 1,
    docStatus: 'D3',
    matStatus: 'A4'
  },
  {
    canonicalName: 'Clarity Metrics (DL, EUR, FR)',
    encodedId: 'oqf.tool.clarity-metrics',
    shortCode: 'OQF-MET',
    elements: '3 core metrics: Decision Latency (speed), Evidence Upgrade Rate (depth), Freshness Rate (discipline)',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    category: 'Type 2: Clarity & Alignment',
    version: 1,
    docStatus: 'D3',
    matStatus: 'A4'
  },
  {
    canonicalName: 'OQF-Lite',
    encodedId: 'oqf.tool.lite',
    shortCode: 'OQF-L',
    elements: '4-step quick method: 1) What\'s the real question? 2) What do we think is true? 3) How could we find out fast? 4) What did we learn?',
    section: 'P-Stack: Prioritization Frameworks',
    sectionCode: 'pf',
    subcategory: 'Capabilities & Diagnostics',
    category: 'Type 2: Clarity & Alignment',
    version: 1,
    docStatus: 'D3',
    matStatus: 'A4'
  }
];

// Group entries by section
export const registrySections = [
  {
    id: 'gf',
    name: 'G-Stack: General Frameworks',
    description: 'Foundational frameworks applicable across domains',
    color: 'sage'
  },
  {
    id: 'pf',
    name: 'P-Stack: Prioritization Frameworks',
    description: 'Core frameworks and models for prioritization practice',
    color: 'blue'
  },
  {
    id: 'pa',
    name: 'P-Stack: App Frameworks',
    description: 'Application-specific modeling and architecture',
    color: 'orange'
  },
  {
    id: 'meta',
    name: 'Meta Stack & Perception',
    description: 'Foundational representational grammar and perception models',
    color: 'gray'
  },
  {
    id: 'cont',
    name: 'Content & Services',
    description: 'Content structures and service offerings',
    color: 'teal'
  }
];

export const getEntriesBySection = (sectionCode: string) => {
  return masterRegistryEntries.filter(entry => entry.sectionCode === sectionCode);
};
